import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../../hooks/useAuth'
import { db, isAbortError } from '../../lib/supabase'
import { getLocalDraft } from '../../hooks/useSurveyForm'
import { ErrorAlert } from '../../components/ui'

export default function ClientDashboard() {
  const { t } = useTranslation('admin')
  const { user } = useAuth()
  const [pair, setPair] = useState(null)
  const [respondent, setRespondent] = useState(null)
  const [hasDraft, setHasDraft] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const loadData = async () => {
      try {
        const [pairData, respondentData, serverDraft] = await Promise.all([
          db.getMyClientPair(user.id),
          db.getMyRespondent(user.id),
          db.getDraft(user.id),
        ])
        if (!cancelled) {
          setPair(pairData)
          setRespondent(respondentData)
          setHasDraft(!!(serverDraft || getLocalDraft(user.id)))
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (user?.id) loadData()
    return () => { cancelled = true }
  }, [user?.id])

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('clientDashboard.loadingDashboard')}</div>
  }

  const surveyCompleted = !!respondent?.completed_at
  const partnerCompleted = pair && (
    (pair.male_client_id === user.id && !!pair.female_survey_completed_at) ||
    (pair.female_client_id === user.id && !!pair.male_survey_completed_at)
  )
  const bothCompleted = surveyCompleted && partnerCompleted

  return (
    <div className="max-w-2xl mx-auto animate-fadeIn">
      <ErrorAlert message={error} onDismiss={() => setError(null)} />

      <div className="text-center mb-8">
        <h2 className="font-display text-2xl font-medium text-gray-800 mb-2">
          {t('clientDashboard.title')}
        </h2>
        <p className="text-gray-500">{t('clientDashboard.subtitle')}</p>
      </div>

      {!surveyCompleted ? (
        <div className="bg-white rounded-xl p-8 shadow-sm text-center">
          <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="font-display text-xl font-medium text-gray-800 mb-3">
            {hasDraft ? t('clientDashboard.survey.continueTitle') : t('clientDashboard.survey.readyTitle')}
          </h3>
          <p className="text-gray-600 mb-6">
            {hasDraft
              ? t('clientDashboard.survey.continueMessage')
              : t('clientDashboard.survey.readyMessage')}
          </p>
          <Link
            to="/client/survey"
            className="inline-block px-8 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5"
          >
            {hasDraft ? t('clientDashboard.survey.continueButton') : t('clientDashboard.survey.startButton')}
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-green-50 rounded-xl p-6 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-display text-lg font-medium text-green-800 mb-2">
              {t('clientDashboard.completed.title')}
            </h3>
            <p className="text-green-700 text-sm mb-4">
              {t('clientDashboard.completed.message')}
            </p>
            <Link
              to="/client/results"
              className="inline-block px-6 py-2.5 text-green-700 bg-green-100 rounded-lg font-semibold hover:bg-green-200 transition-colors"
            >
              {t('clientDashboard.completed.viewResults')}
            </Link>
          </div>

          {bothCompleted ? (
            <div className="bg-primary-50 rounded-xl p-6 text-center">
              <h3 className="font-display text-lg font-medium text-primary-800 mb-2">
                {t('clientDashboard.bothCompleted.title')}
              </h3>
              <p className="text-primary-700 text-sm">
                {t('clientDashboard.bothCompleted.message')}
              </p>
            </div>
          ) : (
            <div className="bg-amber-50 rounded-xl p-6 text-center">
              <h3 className="font-display text-lg font-medium text-amber-800 mb-2">
                {t('clientDashboard.waitingForPartner.title')}
              </h3>
              <p className="text-amber-700 text-sm">
                {t('clientDashboard.waitingForPartner.message')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
